import { Injectable, inject, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { SOCKET_CONFIG, SocketEvents } from '../../types/socket-events.types';
import { AuthenticationStubService } from '../../platform-stubs';

/**
 * Socket Service for WOPI Server Communication
 * 
 * Manages WebSocket connection to the WOPI server for API proxy functionality.
 * 
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Heartbeat monitoring
 * - Event emission and listening
 */
@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private heartbeatInterval: any;
  private authService = inject(AuthenticationStubService);

  // Connection state signals
  public connected = signal<boolean>(false);
  public connecting = signal<boolean>(false);
  public error = signal<string | null>(null);

  /**
   * Connect to WOPI server WebSocket
   */
  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    this.connecting.set(true);
    this.error.set(null);
    const currentUser = this.authService.getCurrentSocketUser();

    const userId =  currentUser.userId;
    const tenantId = currentUser.tenantId;
    // Use wopiUrl from environment for WOPI server connection
    const wopiServerUrl = (environment as any).wopiUrl || SOCKET_CONFIG.DEFAULT_WOPI_URL;
    this.socket = io(wopiServerUrl, {
      transports: ['websocket', 'polling'],
          query: {
        userId: userId,           // ⚠️ CRITICAL: Pass userId
        tenantId: tenantId || ''  // ⚠️ CRITICAL: Pass tenantId
      },
      reconnection: true,
      reconnectionAttempts: SOCKET_CONFIG.RECONNECTION_ATTEMPTS,
      reconnectionDelay: SOCKET_CONFIG.RECONNECTION_DELAY,
      timeout: 10000,
    });

    this.setupEventListeners();
  }

  /** Start the socket if needed and wait until the WOPI connection is ready. */
  async connectAndWait(timeoutMs = 10000): Promise<void> {
    if (this.isConnected()) {
      return;
    }

    if (!this.socket) {
      this.connect();
    }

    const socket = this.socket;
    if (!socket) {
      throw new Error('Unable to initialize the WOPI socket');
    }

    await new Promise<void>((resolve, reject) => {
      let latestError: Error | undefined;
      const cleanup = (): void => {
        clearTimeout(timeoutId);
        socket.off(SocketEvents.CONNECT, onConnect);
        socket.off(SocketEvents.CONNECT_ERROR, onConnectError);
      };
      const onConnect = (): void => {
        cleanup();
        resolve();
      };
      const onConnectError = (error: Error): void => {
        latestError = error;
      };
      const timeoutId = setTimeout(() => {
        cleanup();
        const details = latestError?.message ? `: ${latestError.message}` : '';
        reject(new Error(`Failed to connect to WOPI server${details}`));
      }, timeoutMs);

      socket.on(SocketEvents.CONNECT, onConnect);
      socket.on(SocketEvents.CONNECT_ERROR, onConnectError);
      if (socket.connected) {
        onConnect();
      }
    });
  }

  /**
   * Disconnect from WOPI server
   */
  disconnect(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connected.set(false);
    this.connecting.set(false);
  }

  /**
   * Emit event to server
   */
  emit<T = any>(eventName: string, data: T): void {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit(eventName, data);
  }

  /**
   * Listen for events from server
   */
  on<T = any>(eventName: string, callback: (data: T) => void): void {
    if (!this.socket) {
      return;
    }

    this.socket.on(eventName, callback);
  }

  /**
   * Remove event listener
   */
  off(eventName: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.off(eventName, callback);
    } else {
      this.socket.off(eventName);
    }
  }

  /**
   * Join a file-specific room for isolated events
   * CRITICAL: Call this when opening a file to prevent cross-file event pollution
   */
  joinFileRoom(fileId: string, userId: string, tenantId?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      // Set timeout - must be declared before listeners to be accessible in cleanup
      const timeoutId = setTimeout(() => {
        this.socket?.off(SocketEvents.FILE_ROOM_JOINED, onRoomJoined);
        this.socket?.off(SocketEvents.FILE_ROOM_ERROR, onRoomError);
        reject(new Error('Timeout joining file room'));
      }, 5000);

      // Listen for room joined confirmation
      const onRoomJoined = (response: any) => {
        // CRITICAL: Both must be strings for comparison
        const responseFileId = String(response.fileId);
        const requestFileId = String(fileId);
        
        if (responseFileId === requestFileId && response.success) {
          clearTimeout(timeoutId); // CRITICAL: Clear timeout to prevent race condition
          this.socket?.off(SocketEvents.FILE_ROOM_JOINED, onRoomJoined);
          this.socket?.off(SocketEvents.FILE_ROOM_ERROR, onRoomError);
          resolve();
        }
      };

      const onRoomError = (error: any) => {
        clearTimeout(timeoutId); // CRITICAL: Clear timeout
        this.socket?.off(SocketEvents.FILE_ROOM_JOINED, onRoomJoined);
        this.socket?.off(SocketEvents.FILE_ROOM_ERROR, onRoomError);
        reject(error);
      };

      this.socket.on(SocketEvents.FILE_ROOM_JOINED, onRoomJoined);
      this.socket.on(SocketEvents.FILE_ROOM_ERROR, onRoomError);

      // Emit join request
      this.socket.emit(SocketEvents.FILE_JOIN_ROOM, {
        fileId,
        userId,
        tenantId,
      });
    });
  }

  /**
   * Leave a file-specific room
   * CRITICAL: Call this in ngOnDestroy to clean up listeners and prevent memory leaks
   */
  leaveFileRoom(fileId: string, userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        resolve(); // Socket already disconnected, nothing to do
        return;
      }

      if (!this.socket.connected) {
        resolve(); // Not connected, consider it left
        return;
      }

      // Listen for room left confirmation
      const onRoomLeft = (response: any) => {
        if (response.fileId === fileId && response.success) {
          this.socket?.off(SocketEvents.FILE_ROOM_LEFT, onRoomLeft);
          resolve();
        }
      };

      this.socket.on(SocketEvents.FILE_ROOM_LEFT, onRoomLeft);

      // Emit leave request
      this.socket.emit(SocketEvents.FILE_LEAVE_ROOM, {
        fileId,
        userId,
      });

      // Timeout after 3 seconds
      setTimeout(() => {
        this.socket?.off(SocketEvents.FILE_ROOM_LEFT, onRoomLeft);
        resolve(); // Resolve anyway, don't block component destruction
      }, 3000);
    });
  }

  /**
   * Setup event listeners for connection management
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on(SocketEvents.CONNECT, () => {
      this.connected.set(true);
      this.connecting.set(false);
      this.reconnectAttempts = 0;
      this.error.set(null);
      this.startHeartbeat();
    });

    this.socket.on(SocketEvents.DISCONNECT, (reason: string) => {
      this.connected.set(false);
      this.connecting.set(false);

      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
      }
    });

    this.socket.on(SocketEvents.CONNECT_ERROR, (error: Error) => {
      this.reconnectAttempts++;
      this.error.set(`Connection error: ${error.message}`);

      if (this.reconnectAttempts >= SOCKET_CONFIG.RECONNECTION_ATTEMPTS) {
        this.connecting.set(false);
      }
    });

    this.socket.on(SocketEvents.RECONNECT, (attemptNumber: number) => {
      this.reconnectAttempts = 0;
    });

    this.socket.on(SocketEvents.RECONNECT_ATTEMPT, (attemptNumber: number) => {
    });

    this.socket.on(SocketEvents.RECONNECT_ERROR, (error: Error) => {
    });

    this.socket.on(SocketEvents.RECONNECT_FAILED, () => {
      this.connecting.set(false);
      this.error.set('Failed to reconnect to WOPI server');
    });

    // Listen for connection ready event
    this.socket.on(SocketEvents.CONNECTION_READY, (data: any) => {
    });

    // Listen for heartbeat
    this.socket.on(SocketEvents.HEARTBEAT, () => {
      this.socket?.emit(SocketEvents.HEARTBEAT_ACK, { timestamp: Date.now() });
    });

    // 🐛 DEBUG: Listen for ANY event from server
    this.socket.onAny((eventName: string, ...args: any[]) => {
    });
  }

  /**
   * Start heartbeat monitoring
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Respond to server heartbeats
    // The server sends heartbeats, we just respond
  }

  /**
   * Get socket instance (for advanced usage)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}
